import React, { useState, useEffect } from 'react';

export interface ICclCurrencyInput {
  id: string;
  className?: string;
  value?: number;
  defaultValue?: number;
  currencyPrefix?: string;
  onChange(value: number): void;
}

// CclCurrencyInput component for handling currency-formatted input
export default function CclCurrencyInput({ id, className, value, defaultValue, currencyPrefix = '$', onChange }: ICclCurrencyInput) {
  // Internal state to manage the displayed input value, including the prefix
  const [displayValue, setDisplayValue] = useState('');

  // Effect to update displayValue when the 'value' prop changes from parent
  useEffect(() => {
    if (value !== undefined && value !== null) {
      // If value is provided, format it with the currency prefix
      setDisplayValue(`${currencyPrefix}${value}`);
    } else if (defaultValue !== undefined && defaultValue !== null) {
      // If defaultValue is provided, format it with the currency prefix
      setDisplayValue(`${currencyPrefix}${defaultValue}`);
    } else {
      // If neither is provided, just show the currency prefix
      setDisplayValue(currencyPrefix);
    }
  }, [value, defaultValue, currencyPrefix]);

  // Handle changes to the input field
  const handleChange = (event) => {
    const rawValue = event.target.value;

    // Remove the currency prefix for internal processing
    let numericValue = rawValue.startsWith(currencyPrefix)
      ? rawValue.substring(currencyPrefix.length)
      : rawValue;

    // Remove any non-digit characters except for a single decimal point
    numericValue = numericValue.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point exists
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      numericValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Update the internal display value
    setDisplayValue(`${currencyPrefix}${numericValue}`);

    // Call the parent's onChange handler with the cleaned numeric value
    if (onChange) {
      // Convert to a number if possible, otherwise return an empty string
      onChange(numericValue === '' ? 0 : parseFloat(numericValue));
    }
  };

  // Handle focus event: when the input is focused, remove the currency prefix
  const handleFocus = (event) => {
    const currentNumericValue = displayValue.startsWith(currencyPrefix)
      ? displayValue.substring(currencyPrefix.length)
      : displayValue;
    setDisplayValue(currentNumericValue);
    // Select all text in the input field
    setTimeout(() => {
      event.target.select();      
    }, 250);
  };

  // Handle blur event: when the input loses focus, re-add the currency prefix if there's a value
  const handleBlur = (event) => {
    const rawValue = event.target.value; // Get the raw value from the input field

    // Remove the currency prefix if it exists, to get the pure numeric string
    let numericPart = rawValue.startsWith(currencyPrefix)
      ? rawValue.substring(currencyPrefix.length)
      : rawValue;

    // Clean up any non-numeric characters that might have been typed
    numericPart = numericPart.replace(/[^0-9.]/g, '');

    if (numericPart !== '') {
      // If there's a numeric part, re-add the prefix
      setDisplayValue(`${currencyPrefix}${numericPart}`);
    } else {
      // If the input is empty on blur, just show the currency prefix
      setDisplayValue(currencyPrefix);
    }
  };

  return (
    <input
      id={id}
      type="text" // Use text type to allow handling of prefix
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={className}
      placeholder={`${currencyPrefix}0.00`}
    />
  );
};
